import $ from 'jquery';
import {isEnterEvent, isEscapeEvent, shuffleArray, checkWrittenAnswer, cardTextOnly} from "./playUtil.es6";
import Texter from "./texter.es6";
import PlayProgressBar from "./playProgressBar.es6";
import CardSound from "./cardSound.es6";
import './../lib/jquery/jquery.textchange.js';

require('jsrender')($);

class GameSpell {
    constructor(opts) {
        this.l18n = Texter.create(opts.l18n);

        this.QUESTION_HTML = $.templates(`
<div class="fl-spell-game__question" >
    <div class="fl-spell-game__question-content">
        <div class="fl-spell-game__question-audio" data-lang="{{>lang}}">
                {{if audio}}<audio src="{{attr:audio}}"></audio>{{/if}}
                <p style="display: none;">{{> audioTextOnly}}</p>
                <button type="button" data-click="playAudio" class="btn btn-light">
                    <span class="fal fa-volume-up"></span>
                </button>
        </div>
        <div class="fl-spell-game__question-text">
            <p>{{> questionTextOnly}}</p>
        </div>
    </div>
</div>        
`);

        this.RESULT_SHOW_EXPECTED = $.templates(`
<div class="d-flex">
    <div class="col">
        <span class="fl-write-card__answer-notice">${this.l18n.get('learnPage.result.correct')}</span>
        <p class="fl-write-card__highlight-correct fl-keep-nl text-center">{{>expected}}</p>
    </div>
</div>
<div class="text-center fl-write-card__buttons">
    <button type="button" class="btn btn-primary" data-click="next">${this.l18n.get('learnPage.next')}</button>
</div>
`);

        this.RESULT_SHOW_ERROR = $.templates(`
<div class="d-flex">
    <div class="col">
        <span class="fl-write-card__answer-notice">${this.l18n.get('learnPage.result.correct')}</span>
        <p class="fl-write-card__highlight-correct fl-keep-nl text-center" data-bind="expected">{{>expected}}</p>
    </div>
{{if actual}}
    <div class="col">
        <span class="fl-write-card__answer-notice">${this.l18n.get('learnPage.result.actual')}</span>
        <p class="fl-write-card__highlight-wrong fl-keep-nl text-center" data-bind="actual">{{>actual}}</p>
    </div>
{{/if}}
</div>
<div class="text-center fl-write-card__buttons">
{{if actual}}
    <button type="button" class="btn btn-link" data-click="forceCorrect">${this.l18n.get('learnPage.wrong.stillCorrect')}</button>
{{/if}}
    <button type="button" class="btn btn-primary" data-click="next">${this.l18n.get('learnPage.next')}</button>
</div>
`);

        this.RESULT_SHOW_RESTART = $.templates(`
<div class="my-5 text-center">
    <p style="font-size: 200%;margin-bottom: 0;">${this.l18n.get('learnPage.emptyQueue')}</p>
</div>
<div class="text-center">
    <button type="button" class="btn btn-primary" data-click="restart">${this.l18n.get('learnPage.restartQueue')}</button>
</div>
`);

        this.playProgress = new PlayProgressBar(opts.playProgress, this.l18n, `
${PlayProgressBar.createTitleTextElement(this.l18n.get('learnPage.progress.total'))}
${PlayProgressBar.createTitleTextElement(this.l18n.get('learnPage.progress.correct'), 'correctProgress')}
${PlayProgressBar.createTitleTextElement(this.l18n.get('learnPage.progress.wrong'), 'wrongProgress')}
        `);

        this.cards = opts.cards;
        this.queue = [];
        this.current = null;
        this._started = false;

        this.progress = {
            total: this.cards.length,
            passed: 0, correct: 0, wrong: 0
        };

        this.questionView = opts.questionView;
        this.answerView = opts.answerView;
        this.resultView = opts.resultView;

        this.answerInput = this.answerView.find('input[data-bind=answerText]');
        this.btnGo = this.answerView.find('button[data-click=submit]');

        this.questionView.on('click', 'button[data-click=playAudio]', e => {
            const button = $(e.currentTarget);
            CardSound.singleSideButton(button, button.closest('div'))
                .toggle(complete => this.answerInput.focus());
        });

        this.answerInput
            .on('textchange', () => {
                this._updateSubmitButton();
            })
            .on('keypress', (e) => {
                if (isEnterEvent(e) && !this.btnGo.prop('disabled')) {
                    this.btnGo.click();
                }
            })
            .on('keyup', (e) => {
                if (isEscapeEvent(e)) {
                    this._playCurrent();
                }
            });

        this.btnGo.click(() => {
            this.answer(this.answerInput.val());
        });

        this.answerView.find('button[data-click=giveup]').click(() => {
            this.giveUp();
        });

        this.resultView.on('click', 'button[data-click=next]', () => {
            this.next();
        });
        this.resultView.on('click', 'button[data-click=forceCorrect]', () => {
            this.forceCorrect();
        });
        this.resultView.on('click', 'button[data-click=restart]', () => {
            this.restart();
        });

        CardSound.addReadyCallback(() => {
            this.start();
        });
    }

    _playCurrent() {
        this.questionView.find('button[data-click=playAudio]').get(0).click();
        this.answerInput.focus();
    }

    _updateSubmitButton() {
        this.btnGo.prop('disabled', this.answerInput.val().trim().length === 0);
    }

    start() {
        if (this._started) {
            return;
        }

        this._started = true;
        this.restart();
    }

    restart() {
        this._changeProgress(progress => progress.passed = 0);
        this.queue = [].concat(this.cards);
        shuffleArray(this.queue);

        this.next();
    }

    next() {
        this._display(this._nextCard());
        this._updateSubmitButton();
    }

    _changeProgress(progressCallback) {
        const progress = this.progress;
        progressCallback(progress);

        const count = progress.passed;
        const total = progress.total;

        const percent = total > 0 ? (count / total) : 0;
        this.playProgress.text = `${progress.passed} / ${progress.total}`;
        this.playProgress.percent = percent * 100;
        this.playProgress.setCustomText('correctProgress', progress.correct);
        this.playProgress.setCustomText('wrongProgress', progress.wrong);
    }

    _nextCard() {
        const card = this.queue.pop();
        if (card) {
            return card;
        }

        return null;
    }

    _displayEnd() {
        this.questionView.hide();
        this.resultView
            .append(this.RESULT_SHOW_RESTART.render())
            .show();

        this.resultView.find('button[data-click=restart]').focus();
    }

    _display(card) {
        this.current = null;
        this.resultView.empty().hide();
        CardSound.stop();

        if (card == null) {
            this._displayEnd();
            return;
        }

        this.current = card;

        const htmlModel = {
            lang: card.faceL,
            audio: card.faceA,
            audioText: card.faceText,
            audioTextType: card.faceT === 1 ? 'html' : 'text',
            questionText: card.backText,
            questionTextType: card.backT === 1 ? 'html' : 'text',
        };
        htmlModel.audioTextOnly = cardTextOnly(htmlModel.audioText, htmlModel.audioTextType);
        htmlModel.questionTextOnly = cardTextOnly(htmlModel.questionText, htmlModel.questionTextType);

        this.answerInput.val('');
        this.answerView.show();

        this.questionView
            .empty()
            .append(this.QUESTION_HTML.render(htmlModel))
            .show();
        this._playCurrent();
    }

    _getAnswerText(card) {
        return cardTextOnly(card.faceText, card.faceT === 1 ? 'html' : 'text');
    }

    answer(val) {
        CardSound.stop();
        this.answerView.hide();

        const answer = val.trim();
        const expected = this._getAnswerText(this.current);

        if (answer.length === 0) {
            this._changeProgress(progress => {
                progress.passed += 1;
            });
            this._displayFailure(expected, answer);
        } else if (checkWrittenAnswer(expected, answer)) {
            this._changeProgress(progress => {
                progress.correct += 1;
                progress.passed += 1;
            });
            this._displaySuccess(expected);
        } else {
            this._changeProgress(progress => {
                progress.wrong += 1;
                progress.passed += 1;
            });
            this._displayFailure(expected, answer);
        }

        this.resultView.find('button[data-click=next]').focus();
    }

    forceCorrect() {
        this._changeProgress(progress => {
            progress.wrong -= 1;
            progress.correct += 1;
        });
        this.next();
    }

    giveUp() {
        this.answer('');
    }

    _displaySuccess(expected) {
        const html = this.RESULT_SHOW_EXPECTED.render({
            expected: expected
        });
        this.resultView
            .html(html)
            .show();
    }

    _displayFailure(expected, actual) {
        const html = this.RESULT_SHOW_ERROR.render({
            expected: expected,
            actual: actual
        });
        this.resultView
            .html(html)
            .show();
    }
}

global.GameSpell = GameSpell;